// Controls
import { AiGeneratedDisclaimer } from "@fluentui-copilot/react-copilot-chat";
import { FeedbackButtons } from "@fluentui-copilot/react-feedback-buttons";
// Styles
import { useTheme, styled, classNamesFunction } from "@fluentui/react";
import { getStyles } from "./AIDisclaimerAndFeedback.styles";
// Constants
import { Environment, OfficeBrowserFeedbackConstants } from "@ms/uno-constants/lib/local/AppConstants";
import { AppContext } from "@ms/uno-constants/lib/local/configuration/AppContext";
// Utilities
import React from "react";
import debounce from "lodash/debounce";
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import { useLoggers } from "../../../hooks/useLoggers";
import { getCopilotFeedbackOptions, getFeedbackInitData, openInAppFeedback, unmountInAppFeedback } from "@ms/uno-office-feedback/lib/local/InAppFeedbackUtilities";
const FeedbackHostId = "inAppFeedbackRef-id";
// Feedback type
export var FeedbackType = /*#__PURE__*/ function(FeedbackType) {
    FeedbackType["Positive"] = "positive";
    FeedbackType["Negative"] = "negative";
    return FeedbackType;
}({});
/** Callback to generate classnames for the component styles */ const getClassNames = classNamesFunction();
/** Render the AI content disclaimer */ const AIDisclaimerAndFeedbackBase = (props)=>{
    const { idPrefix = "", tenantFeedbackSettings, onLogLikeDislike, getFeedbackContextData, styles, context, feedbackFeatureArea, feedbackFeatureName } = props;
    const theme = useTheme();
    const classNames = getClassNames(styles);
    const loggerContext = useLoggers();
    const [feedbackButtonSelectedState, setFeedbackButtonSelectedState] = React.useState(undefined);
    const inAppFeedbackElementId = `${idPrefix}${FeedbackHostId}`;
    /**
     * Debounced log method to avoid multiple logs if user clicks back and forth quickly
     * @param feedbackSelected The feedback to log
     */ const logCopilotLikeDislike = React.useMemo(()=>debounce((feedbackSelected)=>{
            if (feedbackSelected == null) {
                return;
            }
            const isPositive = feedbackSelected === "positive";
            onLogLikeDislike(isPositive);
        }, 3000), [
        onLogLikeDislike
    ]);
    /**
     * Callback when Copilot feedback button is selected
     * @param feedbackSelected The feedback selected by the user
     */ const onClickCopilotFeedback = React.useCallback(async (feedbackSelected)=>{
        // If state is already the same as the clicked state, set it to undefined
        const feedbackStateToSet = feedbackButtonSelectedState === feedbackSelected ? undefined : feedbackSelected;
        setFeedbackButtonSelectedState(feedbackStateToSet);
        logCopilotLikeDislike(feedbackStateToSet);
        // Only open feedback if tenant settings allow feedback
        if (tenantFeedbackSettings?.allowFeedback && feedbackStateToSet !== undefined) {
            const feedbackInitData = getFeedbackInitData(context.locale, inAppFeedbackElementId);
            const feedbackOptions = getCopilotFeedbackOptions({
                feedbackFeatureArea: feedbackFeatureArea,
                feedbackFeatureName: feedbackFeatureName,
                isPositive: feedbackStateToSet === "positive",
                theme,
                tenantFeedbackSettings: {
                    ...tenantFeedbackSettings,
                    allowCopilotFeedback: false,
                    allowConnectedOfficeExperiences: false,
                    getPolicyDetails: ()=>undefined
                },
                appId: OfficeBrowserFeedbackConstants.AppId,
                isProduction: AppContext.appConfiguration.sessionMetaData.environment === Environment.Prod,
                currentUserId: context.userId,
                tenantId: context.tenantId,
                sessionId: context.sessionId,
                locale: context.locale,
                getContextData: getFeedbackContextData,
                onDismiss: ()=>{
                    unmountInAppFeedback(loggerContext);
                }
            });
            openInAppFeedback(feedbackInitData, feedbackOptions, loggerContext);
        }
    }, [
        feedbackButtonSelectedState,
        logCopilotLikeDislike,
        theme,
        tenantFeedbackSettings,
        inAppFeedbackElementId,
        getFeedbackContextData,
        context,
        loggerContext,
        feedbackFeatureArea,
        feedbackFeatureName
    ]);
    return /*#__PURE__*/ React.createElement("div", {
        className: classNames.root
    }, /*#__PURE__*/ React.createElement(AiGeneratedDisclaimer, null, Strings.Copilot_AIContentDisclaimer), /*#__PURE__*/ React.createElement(FeedbackButtons, {
        selected: feedbackButtonSelectedState,
        positiveFeedbackButton: {
            onClick: ()=>onClickCopilotFeedback("positive"),
            "aria-roledescription": feedbackButtonSelectedState === "positive" ? Strings.Copilot_AIContentDisclaimerButtonSelectedAriaLabel : undefined
        },
        positiveFeedbackTooltip: {
            content: Strings.Common_Like
        },
        negativeFeedbackButton: {
            onClick: ()=>onClickCopilotFeedback("negative"),
            "aria-roledescription": feedbackButtonSelectedState === "negative" ? Strings.Copilot_AIContentDisclaimerButtonSelectedAriaLabel : undefined
        },
        negativeFeedbackTooltip: {
            content: Strings.Common_Dislike
        }
    }), /*#__PURE__*/ React.createElement("div", {
        id: inAppFeedbackElementId
    }));
};
/**
 * Render the AI content disclaimer and feedback buttons
 */ export const AIDisclaimerAndFeedbackV9 = styled(AIDisclaimerAndFeedbackBase, getStyles);
